import { DeviceStatusString } from '@emfit/shared-types';
import { Badge, BadgeProps } from '../Badge';
import { getStatus } from '../../utils';

export interface DeviceStatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  app: 'qs' | 'care';
  status: number;
  pulse?: boolean;
  label?: string;
  /** Translates the default label; defaults to the English key */
  t?: (key: string) => string;
}

const statusConfig: Record<
  DeviceStatusString,
  { variant: BadgeProps['variant']; pulseColor: string }
> = {
  online: { variant: 'success', pulseColor: 'bg-green-500' },
  offline: { variant: 'neutral', pulseColor: 'bg-gray-500' },
  warning: { variant: 'warning', pulseColor: 'bg-yellow-500' },
  error: { variant: 'error', pulseColor: 'bg-red-500' },
  inBed: { variant: 'success', pulseColor: 'bg-green-500' },
  outOfBed: { variant: 'warning', pulseColor: 'bg-yellow-500' },
};

/**
 * Written as literal t() calls so each app's dictionary script picks the
 * keys up; they match the sentence-case keys both apps already translate.
 */
const statusLabels = (
  t: (key: string) => string
): Record<DeviceStatusString, string> => ({
  online: t('Online'),
  offline: t('Offline'),
  warning: t('Warning'),
  error: t('Error'),
  inBed: t('In bed'),
  outOfBed: t('Out of bed'),
});

const identity = (key: string) => key;

/**
 * DeviceStatusBadge component
 *
 * Unified status badge for devices with optional pulse animation
 * Supports both text badges and icon modes
 */
export const DeviceStatusBadge: React.FC<DeviceStatusBadgeProps> = ({
  app,
  status,
  label,
  t = identity,
  ...badgeProps
}) => {
  const state = getStatus(app, status);
  const config = statusConfig[state];
  const displayLabel = label || statusLabels(t)[state];

  return (
    <Badge variant={config.variant} {...badgeProps}>
      {displayLabel}
    </Badge>
  );
};
